<template>
  <Teleport to="body">
    <Transition name="pip-pop">
      <div
        v-if="player.isOpen.value && player.current.value"
        ref="containerRef"
        class="fixed z-[9990] bg-black rounded-xl overflow-hidden shadow-2xl border border-gray-700 flex flex-col"
        :style="containerStyle">
        <!-- Title bar (drag handle) -->
        <div
          class="flex items-center justify-between px-3 py-1.5 bg-gray-900 cursor-grab active:cursor-grabbing select-none shrink-0"
          @mousedown="startDrag">
          <div class="flex items-center gap-2 min-w-0">
            <span class="material-symbols-outlined text-sm text-gray-400">videocam</span>
            <span class="text-xs text-gray-300 truncate">{{ player.current.value.cameraName }}</span>
          </div>
          <div class="flex items-center gap-1 flex-shrink-0">
            <!-- Expand to modal -->
            <button
              @click="expandToModal"
              class="text-gray-400 hover:text-white p-0.5 transition-colors"
              title="Expand">
              <span class="material-symbols-outlined text-sm">open_in_full</span>
            </button>
            <button
              @click="player.close()"
              class="text-gray-400 hover:text-white p-0.5 transition-colors"
              title="Close">
              <span class="material-symbols-outlined text-sm">close</span>
            </button>
          </div>
        </div>

        <!-- Video -->
        <div class="flex-1 relative bg-black min-h-0">
          <WebRTCPlayer
            :camera-id="player.current.value.cameraId"
            :poster-src="playerPosterSnapshot"
            :auto-connect="true"
            :muted="true"
            :controls="false"
            :show-status="true"
            :show-retry="true"
            :show-call="true"
            class="w-full h-full" />
        </div>

        <!-- Resize handle -->
        <div class="absolute bottom-0 right-0 w-4 h-4 cursor-nwse-resize z-10" @mousedown.stop="startResize">
          <svg class="w-full h-full text-gray-600" viewBox="0 0 16 16" fill="currentColor">
            <circle cx="12" cy="12" r="1.5" />
            <circle cx="8" cy="12" r="1.5" />
            <circle cx="12" cy="8" r="1.5" />
          </svg>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, onUnmounted, watch } from 'vue';
import WebRTCPlayer from './WebRTCPlayer.vue';
import { useCameraPlayer } from '../composables/useCameraPlayer';
import { useCameraModal } from '../composables/useCameraModal';
import { useCameraSnapshotsStore } from '../stores/cameraSnapshots';

const player = useCameraPlayer();
const modal = useCameraModal();
const cameraSnapshotsStore = useCameraSnapshotsStore();

const containerRef = ref<HTMLElement | null>(null);
const playerPosterSnapshot = computed(() =>
  player.current.value ? cameraSnapshotsStore.getSnapshotUrl(player.current.value.cameraId) : null,
);

const containerStyle = computed(() => ({
  left: `${player.position.value.x}px`,
  top: `${player.position.value.y}px`,
  width: `${player.position.value.w}px`,
  height: `${player.position.value.h}px`,
}));

watch(
  () => [player.isOpen.value, player.current.value?.cameraId] as const,
  ([open, cameraId]) => {
    if (!open || !cameraId) return;
    void cameraSnapshotsStore.fetchSnapshot(cameraId);
  },
  { immediate: true },
);

// -- Drag --
let dragOffset = { x: 0, y: 0 };
let isDragging = false;

function startDrag(e: MouseEvent) {
  isDragging = true;
  dragOffset = {
    x: e.clientX - player.position.value.x,
    y: e.clientY - player.position.value.y,
  };
  window.addEventListener('mousemove', onDrag);
  window.addEventListener('mouseup', stopDrag);
}

function onDrag(e: MouseEvent) {
  if (!isDragging) return;
  const x = Math.max(0, Math.min(window.innerWidth - 100, e.clientX - dragOffset.x));
  const y = Math.max(0, Math.min(window.innerHeight - 50, e.clientY - dragOffset.y));
  player.updatePosition({ x, y });
}

function stopDrag() {
  isDragging = false;
  window.removeEventListener('mousemove', onDrag);
  window.removeEventListener('mouseup', stopDrag);
}

// -- Resize --
let isResizing = false;

function startResize() {
  isResizing = true;
  window.addEventListener('mousemove', onResize);
  window.addEventListener('mouseup', stopResize);
}

function onResize(e: MouseEvent) {
  if (!isResizing) return;
  const w = Math.max(240, Math.min(800, e.clientX - player.position.value.x));
  const h = Math.max(160, Math.min(600, e.clientY - player.position.value.y));
  player.updatePosition({ w, h });
}

function stopResize() {
  isResizing = false;
  window.removeEventListener('mousemove', onResize);
  window.removeEventListener('mouseup', stopResize);
}

// -- Expand to modal --
function expandToModal() {
  if (!player.current.value) return;
  const cam = player.current.value;
  player.close();
  modal.open({
    cameraId: cam.cameraId,
    cameraName: cam.cameraName,
    hasPtz: false, // PiP doesn't track PTZ capability, modal will query
    hasAudio: false,
  });
}

onUnmounted(() => {
  window.removeEventListener('mousemove', onDrag);
  window.removeEventListener('mouseup', stopDrag);
  window.removeEventListener('mousemove', onResize);
  window.removeEventListener('mouseup', stopResize);
});
</script>

<style scoped>
.pip-pop-enter-active,
.pip-pop-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}
.pip-pop-enter-from,
.pip-pop-leave-to {
  opacity: 0;
  transform: scale(0.9) translateY(10px);
}
</style>
